// Oil and gas pipelines (066): OpenStreetMap reference lines, drawn from our
// own vector tiles (/api/tiles/pipelines) under the objects, coloured by what
// they carry. Refineries and oil & gas facilities are PLACES — entity types in
// the Energy category, on the entity tiles like any place; this is only the
// lines between them, which are geography nobody edits.
//
// The server fills the table from Overpass one tile of the theatre at a time
// after it boots, so the layer can be half-drawn for an hour or two; the Layers
// row says how far it has got (energyStatus) rather than showing a partial map
// as though it were the whole one.

import type * as maplibregl from 'maplibre-gl';
import type { LayerSpecification, VectorTileSource } from 'maplibre-gl';
import { get } from './api';

const SRC = 'pipelines';
export const PIPELINE_LAYERS = ['pipelines-casing', 'pipelines-line', 'pipelines-label'];

/** What flows, as a colour: oil dark brown, gas amber, refined fuel red-orange,
 *  anything else violet, and grey where the mapper did not say (most desert
 *  trunk lines). */
export const SUBSTANCE_COLORS: Record<string, string> = {
  oil: '#5b3a1e', gas: '#d69e00', fuel: '#c2410c', other: '#7c3aed',
};
export const UNKNOWN_COLOR = '#7b8794';

const width = (base: number): unknown => ['interpolate', ['linear'], ['zoom'],
  4, 0.5 * base, 7, 1 * base, 10, 1.8 * base, 14, 3 * base];

/** The layer specs, pure so the style-spec test can validate them — a
 *  malformed expression fails inside addLayer, where nothing reports it. */
export function pipelineLayers(): LayerSpecification[] {
  const color = ['match', ['get', 'substance'],
    ...Object.entries(SUBSTANCE_COLORS).flat(), UNKNOWN_COLOR];
  return [
    {
      // A pale halo, so a brown line still reads over dark imagery.
      id: 'pipelines-casing', type: 'line', source: SRC, 'source-layer': 'pipelines',
      minzoom: 5,
      layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
      paint: { 'line-color': 'rgba(255,255,255,0.55)', 'line-width': width(2.6) as never },
    },
    {
      id: 'pipelines-line', type: 'line', source: SRC, 'source-layer': 'pipelines',
      layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
      paint: {
        'line-color': color as never,
        'line-width': width(1.3) as never,
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 9, 0.95] as never,
      },
    },
    {
      id: 'pipelines-label', type: 'symbol', source: SRC, 'source-layer': 'pipelines',
      minzoom: 8,
      filter: ['has', 'name'],
      layout: {
        visibility: 'none',
        'symbol-placement': 'line',
        'text-field': ['get', 'name'],
        // OpenFreeMap serves the Noto family only (see markup.ts).
        'text-font': ['Noto Sans Regular'],
        'text-size': 10.5,
        'symbol-spacing': 420,
      },
      paint: {
        'text-color': color as never,
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.4,
      },
    },
  ] as LayerSpecification[];
}

let on = false;

/** Add the source and layers (hidden) under the object layers. */
export function installPipelines(map: maplibregl.Map): void {
  if (map.getSource(SRC)) return;
  map.addSource(SRC, {
    type: 'vector',
    tiles: [tileUrl()],
    minzoom: 3,
    maxzoom: 14,
    attribution: 'Pipelines © OpenStreetMap contributors (ODbL)',
  });
  const before = map.getLayer('entities-fill') ? 'entities-fill' : undefined;
  for (const layer of pipelineLayers()) map.addLayer(layer, before);
}

const tileUrl = (v?: number): string =>
  `${location.origin}/api/tiles/pipelines/{z}/{x}/{y}${v === undefined ? '' : `?v=${v}`}`;

/** Re-ask the tiles once more of the theatre has landed: the browser keeps a
 *  tile 15 minutes, and an area that was empty an hour ago may not be now. */
export function bumpPipelines(map: maplibregl.Map, v: number): void {
  (map.getSource(SRC) as VectorTileSource | undefined)?.setTiles([tileUrl(v)]);
}

export function setPipelines(map: maplibregl.Map, show: boolean): void {
  if (!map.getLayer('pipelines-line') || show === on) return;
  on = show;
  for (const id of PIPELINE_LAYERS) {
    map.setLayoutProperty(id, 'visibility', show ? 'visible' : 'none');
  }
}

export interface EnergyStatus {
  tiles: { done: number; failed: number; total: number };
  pipelines: number;
  lastError: string | null;
  loading: boolean;
  autoload: boolean;
}

export function energyStatus(): Promise<EnergyStatus> {
  return get<EnergyStatus>('/api/energy/status');
}

/** The Layers row's count: the lines, and how much of the theatre is in. */
export function statusLabel(s: EnergyStatus): { text: string; title: string } {
  const { done, failed, total } = s.tiles;
  const lines = `${s.pipelines.toLocaleString()} lines`;
  if (done >= total) {
    return { text: lines, title: 'OpenStreetMap pipelines across the theatre.' };
  }
  const part = `${done}/${total} areas`;
  const why = failed && s.lastError
    ? ` Overpass did not answer for ${failed} (${s.lastError.slice(0, 120)}); they are retried.`
    : '';
  return {
    text: done ? `${lines} · ${part}` : s.loading || s.autoload ? `loading · ${part}` : part,
    title: `Filling from OpenStreetMap one area of the theatre at a time — ${part} so far.${why}`,
  };
}
